import type { InviteInfo } from '@shared/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Button } from '../../components/ui/button';
import { Alert, Spinner } from '../../components/ui/feedback';
import { Field, Input } from '../../components/ui/form';
import { api } from '../../lib/api';
import { errorMessage, fieldErrors } from '../../lib/session';
import { AuthLayout } from './auth-layout';

export function InvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [form, setForm] = useState({ name: '', password: '' });

  const invite = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.get<InviteInfo>(`/api/auth/invites/${encodeURIComponent(token)}`),
    enabled: token.length > 0,
    retry: false,
  });

  const accept = useMutation({
    mutationFn: (input: { name?: string; password?: string }) =>
      api.post<{ ok: true }>(`/api/auth/invites/${encodeURIComponent(token)}/accept`, input),
    onSuccess: async () => {
      await client.invalidateQueries();
      void navigate('/', { replace: true });
    },
  });

  if (invite.isPending) {
    return (
      <AuthLayout title="Checking your invite">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner /> Loading…
        </div>
      </AuthLayout>
    );
  }

  if (invite.isError || !invite.data) {
    return (
      <AuthLayout title="Invite unavailable">
        <Alert tone="error">{errorMessage(invite.error) ?? 'This invite link is not valid.'}</Alert>
        <Link className="mt-4 inline-block text-sm text-accent underline-offset-4 hover:underline" to="/login">
          Go to sign in
        </Link>
      </AuthLayout>
    );
  }

  const info = invite.data;
  if (info.accepted || info.expired) {
    return (
      <AuthLayout title="Invite unavailable">
        <Alert tone="warning">{info.accepted ? 'This invite was already accepted.' : 'This invite has expired. Ask for a new one.'}</Alert>
        <Link className="mt-4 inline-block text-sm text-accent underline-offset-4 hover:underline" to="/login">
          Go to sign in
        </Link>
      </AuthLayout>
    );
  }

  if (info.existingUser) {
    return (
      <AuthLayout title={`Join ${info.workspaceName}`} description={`Sign in as ${info.email}, then open this link again to join.`}>
        <Link to="/login">
          <Button className="w-full">Sign in</Button>
        </Link>
      </AuthLayout>
    );
  }

  const errors = fieldErrors(accept.error);
  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    accept.mutate(form);
  };

  return (
    <AuthLayout title={`Join ${info.workspaceName}`} description={`You were invited as ${info.role} (${info.email}).`}>
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        {accept.error ? <Alert tone="error">{errorMessage(accept.error)}</Alert> : null}
        <Field label="Your name" error={errors['name']} required>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              value={form.name}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              invalid={invalid}
              aria-describedby={describedBy}
              autoFocus
              required
            />
          )}
        </Field>
        <Field label="Password" hint="At least 10 characters." error={errors['password']} required>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
              invalid={invalid}
              aria-describedby={describedBy}
              required
            />
          )}
        </Field>
        <Button type="submit" className="w-full" loading={accept.isPending}>
          Join workspace
        </Button>
      </form>
    </AuthLayout>
  );
}
